<template>
  <canvas ref="canvasRef" class="fireworks-canvas" aria-hidden="true" />
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const emit = defineEmits(['complete'])

const canvasRef = ref(null)

const PALETTE = ['#FFD93D', '#FF6B6B', '#36c070', '#2B57EE', '#A855F7', '#22D3EE', '#FF8C42', '#F472B6']
const SALVO_COUNT = 5
const SALVO_INTERVAL_MS = 200

let rafId = 0
let width = 0
let height = 0
let ctx = null
let rockets = []
let particles = []
let frame = 0
let reducedMotion = false
let salvoScheduled = false
let salvoFinished = false
let completedEmitted = false
let idleFrames = 0

const salvoTimers = []

function rand(min, max) {
  return Math.random() * (max - min) + min
}

function pickColor() {
  return PALETTE[Math.floor(Math.random() * PALETTE.length)]
}

function launchRocket(x = rand(width * 0.12, width * 0.88)) {
  const targetY = rand(height * 0.1, height * 0.38)
  rockets.push({
    x,
    y: height + 8,
    targetY,
    speed: rand(7, 11),
    color: pickColor(),
    trail: [],
  })
}

function explode(x, y, color) {
  const count = Math.floor(rand(48, 80))
  for (let i = 0; i < count; i += 1) {
    const angle = (Math.PI * 2 * i) / count + rand(-0.12, 0.12)
    const speed = rand(2.2, 7.5)
    particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      alpha: 1,
      decay: rand(0.014, 0.03),
      gravity: rand(0.04, 0.07),
      friction: 0.985,
      color,
      size: rand(1.4, 2.8),
      sparkle: Math.random() > 0.55,
    })
  }
}

function burstSalvoOnce() {
  if (salvoScheduled) return
  salvoScheduled = true
  for (let i = 0; i < SALVO_COUNT; i += 1) {
    salvoTimers.push(setTimeout(() => launchRocket(), i * SALVO_INTERVAL_MS))
  }
  salvoTimers.push(
    setTimeout(() => {
      salvoFinished = true
    }, SALVO_COUNT * SALVO_INTERVAL_MS + 400),
  )
}

function resize() {
  const canvas = canvasRef.value
  if (!canvas) return
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  width = canvas.clientWidth
  height = canvas.clientHeight
  canvas.width = Math.floor(width * dpr)
  canvas.height = Math.floor(height * dpr)
  ctx = canvas.getContext('2d')
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
}

function tryComplete() {
  if (completedEmitted || !salvoFinished) return
  if (rockets.length > 0 || particles.length > 0) return
  idleFrames += 1
  if (idleFrames < 20) return
  completedEmitted = true
  emit('complete')
  cancelAnimationFrame(rafId)
  rafId = 0
}

function tick() {
  if (!ctx) return
  frame += 1

  ctx.globalCompositeOperation = 'source-over'
  ctx.clearRect(0, 0, width, height)

  rockets = rockets.filter((rocket) => {
    rocket.trail.push({ x: rocket.x, y: rocket.y })
    if (rocket.trail.length > 10) rocket.trail.shift()
    rocket.y -= rocket.speed

    ctx.globalCompositeOperation = 'source-over'
    rocket.trail.forEach((point, idx) => {
      const t = (idx + 1) / rocket.trail.length
      ctx.beginPath()
      ctx.fillStyle = rocket.color
      ctx.globalAlpha = t * 0.35
      ctx.arc(point.x, point.y, 2 * t, 0, Math.PI * 2)
      ctx.fill()
    })
    ctx.globalAlpha = 1

    ctx.strokeStyle = rocket.color
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(rocket.x, rocket.y + 8)
    ctx.lineTo(rocket.x, rocket.y)
    ctx.stroke()

    if (rocket.y <= rocket.targetY) {
      explode(rocket.x, rocket.y, rocket.color)
      return false
    }
    return true
  })

  particles = particles.filter((p) => {
    p.vx *= p.friction
    p.vy *= p.friction
    p.vy += p.gravity
    p.x += p.vx
    p.y += p.vy
    p.alpha -= p.decay

    if (p.alpha <= 0) return false

    ctx.globalCompositeOperation = 'source-over'
    ctx.globalAlpha = Math.max(p.alpha, 0)
    ctx.beginPath()
    ctx.fillStyle = p.color
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
    ctx.fill()

    if (p.sparkle && frame % 3 === 0) {
      ctx.globalAlpha = p.alpha * 0.5
      ctx.strokeStyle = p.color
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(p.x - 3, p.y)
      ctx.lineTo(p.x + 3, p.y)
      ctx.moveTo(p.x, p.y - 3)
      ctx.lineTo(p.x, p.y + 3)
      ctx.stroke()
    }
    return true
  })

  ctx.globalAlpha = 1
  tryComplete()

  if (!completedEmitted) {
    rafId = requestAnimationFrame(tick)
  }
}

function start() {
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  resize()
  if (reducedMotion) {
    emit('complete')
    return
  }
  burstSalvoOnce()
  tick()
}

function stop() {
  cancelAnimationFrame(rafId)
  rafId = 0
  salvoTimers.forEach(clearTimeout)
  salvoTimers.length = 0
  rockets = []
  particles = []
}

let resizeObserver

onMounted(() => {
  const canvas = canvasRef.value
  if (!canvas) return
  resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(canvas)
  window.addEventListener('resize', resize)
  start()
})

onUnmounted(() => {
  stop()
  resizeObserver?.disconnect()
  window.removeEventListener('resize', resize)
})
</script>

<style scoped>
.fireworks-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}
</style>
